import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Hàng nút ở chân hộp thoại. */
  footer?: ReactNode;
  /** Chiều rộng tối đa (px). */
  maxWidth?: number;
  /** Chặn đóng khi đang lưu — tránh mất dữ liệu đang gửi dở. */
  busy?: boolean;
}

/**
 * Khung hộp thoại dùng chung.
 *
 * Mỗi popup trước đây tự chép lại nguyên khối overlay + motion + nút X + chân
 * hộp thoại (~50 dòng/popup), nên sửa hiệu ứng ở một chỗ là lệch với chỗ khác.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 520,
  busy = false,
}: ModalProps) {
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60" onClick={close} />
          <motion.div
            // `max-h` + cột dọc: thân dài hơn màn hình thì CHÍNH thân cuộn, chứ
            // không phải popup tràn ra ngoài viewport rồi mất luôn nút ở chân.
            className="border-line bg-panel relative flex max-h-full w-full flex-col rounded-xl border shadow-2xl"
            style={{ maxWidth }}
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
          >
            <div className="border-line flex shrink-0 items-center justify-between border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">{title}</h3>
              <button
                type="button"
                onClick={close}
                disabled={busy}
                aria-label="Đóng"
                className="text-ink-dim hover:bg-panel-hover hover:text-ink rounded-lg p-1.5 transition-colors disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            {/* Không có footer thì thân PHẢI tự đệm đáy — thiếu `pb-5`, khối cuối
                (ảnh QR ở popup nạp) dính thẳng vào viền dưới của popup. */}
            <div className={`overflow-y-auto px-5 pt-5 ${footer ? '' : 'pb-5'}`}>{children}</div>

            {footer && (
              <div className="border-line mt-5 flex shrink-0 items-center justify-end gap-2 border-t px-5 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
